import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, GitCommit, Sparkles, Wrench, Shield, ChevronRight } from "lucide-react";

/**
 * /changelog — public release history.
 *
 * Source of truth lives in CHANGELOG.md (eventually). For now we curate the
 * shipped iter highlights so customers can see velocity and trust the platform
 * is actively built. Subtle navy/slate, sharp edges, no overpowering colours.
 */
const KIND_META = {
  feature: { label: "Feature",     Icon: Sparkles, accent: "text-[#0055FF]",   bg: "bg-[#0055FF]/8" },
  fix:     { label: "Fix",         Icon: Wrench,   accent: "text-slate-700",    bg: "bg-slate-100" },
  security:{ label: "Security",    Icon: Shield,   accent: "text-emerald-700",  bg: "bg-emerald-50" },
  ops:     { label: "Operational", Icon: GitCommit,accent: "text-amber-700",    bg: "bg-amber-50" },
};

const ENTRIES = [
  {
    date: "12 Feb 2026", version: "iter92",
    title: "CPL waterfall routing on ping trees",
    body: "Ping trees now route inbound leads as a priority waterfall (not auction). Highest-weight eligible buyer wins; cascades through the tree on rejection. Same flat sector CPL applies — no bidding.",
    kind: "feature",
  },
  {
    date: "11 Feb 2026", version: "iter91",
    title: "Buyer business hours + Campaign dashboard",
    body: "Buyers can now declare a delivery window (timezone-aware). Outside the window leads are rejected before any wallet debit. New /dashboard/campaign page exposes status, sector, MTD, today's count, business hours and pause toggle in one view.",
    kind: "feature",
  },
  {
    date: "10 Feb 2026", version: "iter90x",
    title: "Invoice PDF renderer hardened",
    body: "Renderer now accepts both legacy qty and canonical quantity fields, and tolerates missing VAT/subtotal fields. Fixes white error page on signed PDF links for invoices backfilled from pre-iter90 data.",
    kind: "fix",
  },
  {
    date: "9 Feb 2026", version: "iter90w",
    title: "Sandbox API key for publishers",
    body: "Publishers can now test the lead-ingest endpoint with pk_test_sandbox_... before live account approval. Sandbox requests return a realistic response shape but never touch routing or billing.",
    kind: "feature",
  },
  {
    date: "8 Feb 2026", version: "iter90v",
    title: "Developer documentation rebuilt",
    body: "/docs rewritten end-to-end with accurate cURL / Node / Python snippets, error reference, webhook events, rate limits, and HMAC signature verification examples.",
    kind: "feature",
  },
  {
    date: "8 Feb 2026", version: "iter90t",
    title: "Removed hardcoded demo credentials from production login",
    body: "Demo account credentials no longer rendered in the production Login page bundle. Demo logins controlled by REACT_APP_SHOW_DEMO_LOGINS env flag (off in production).",
    kind: "security",
  },
  {
    date: "7 Feb 2026", version: "iter90s",
    title: "Admin Action Centre + daily summary digest",
    body: "/admin/overview now surfaces a single Action Centre card with pending reservations, unsigned contracts, low-balance buyers and stale invoices. Daily 07:00 UTC summary emailed to admins.",
    kind: "feature",
  },
  {
    date: "6 Feb 2026", version: "iter90r",
    title: "Bulk Nudge-all for contracts & invoices",
    body: "Admin can now bulk-nudge every stale contract or invoice with a single click. 24h cooldown per recipient. Dry-run preview shows exactly who will be notified.",
    kind: "feature",
  },
  {
    date: "5 Feb 2026", version: "iter90q",
    title: "EMAIL_OVERRIDE_TO production guard",
    body: "Backend automatically disables the EMAIL_OVERRIDE_TO testing safety guard when APP_URL=https://connex.is. Prevents accidental dev-environment email override from blocking production deliveries.",
    kind: "ops",
  },
  {
    date: "4 Feb 2026", version: "iter90p",
    title: "Manual contract issuance",
    body: "Admins can now issue ad-hoc contracts (DPA / MSA / NDA / custom) to external recipients from /admin/contracts, /admin/users and /admin/publishers. Backfill at signup wires the contract to the new user account automatically.",
    kind: "feature",
  },
];

export default function Changelog() {
  return (
    <div className="bg-white min-h-screen" data-testid="changelog-page">

      {/* Hero */}
      <section className="bg-slate-950 text-white border-b border-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(0,85,255,0.15),transparent_55%)] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 py-20 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/10 bg-white/5 text-[10.5px] uppercase tracking-[0.18em] text-white/65 font-mono mb-7">
            <GitCommit className="size-3" /> Changelog
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold leading-[1.05] max-w-2xl">
            Built in public.<br />
            <span className="text-white/55">Shipped continuously.</span>
          </h1>
          <p className="mt-6 text-[15px] text-white/60 max-w-xl leading-relaxed">
            Every meaningful change to the Connexis platform, with the date it shipped. No marketing fluff — what we built, why, and what it does for you.
          </p>
        </div>
      </section>

      {/* Entries timeline */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <ol className="relative border-l border-slate-200 ml-3">
          {ENTRIES.map((e, i) => {
            const meta = KIND_META[e.kind];
            const M = meta.Icon;
            return (
              <li key={i} className="relative pl-8 pb-10" data-testid={`changelog-entry-${i}`}>
                {/* Bullet */}
                <span className={`absolute -left-[9px] top-1 inline-flex size-4 items-center justify-center rounded-sm ${meta.bg} border border-white`}>
                  <span className={`size-2 rounded-sm ${meta.accent.replace("text-", "bg-")}`} />
                </span>

                <div className="flex items-center gap-3 flex-wrap text-[11.5px] font-mono text-slate-500 mb-2">
                  <span className="uppercase tracking-[0.14em]">{e.date}</span>
                  <span className="text-slate-300">·</span>
                  <span>{e.version}</span>
                  <span className="text-slate-300">·</span>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm ${meta.bg} ${meta.accent} text-[10px] uppercase tracking-[0.14em]`}>
                    <M className="size-2.5" /> {meta.label}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-slate-900 tracking-tight leading-tight">{e.title}</h2>
                <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed max-w-2xl">{e.body}</p>
              </li>
            );
          })}
        </ol>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900">
            Want to see what we're working on next?
          </h2>
          <p className="mt-4 text-[14.5px] text-slate-600 max-w-lg mx-auto">
            The public roadmap shows what's in flight, what's queued for the next quarter, and what we've heard from customers.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800">
              <Link to="/roadmap" data-testid="changelog-roadmap-cta">View roadmap <ArrowUpRight className="size-4 ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-slate-200">
              <Link to="/status" data-testid="changelog-status-cta">System status</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
